import { useEffect, useState, useRef } from "react";
import {
  collection, getDocs, doc, updateDoc, addDoc, deleteDoc,
  query, orderBy
} from "firebase/firestore";
import { db, storage } from "./firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./index.css";

function Expositores() {
  const [exhibitors, setExhibitors] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [newExhibitor, setNewExhibitor] = useState({ name: "", website: "", logo: "" });
  const [logoPreview, setLogoPreview] = useState(null);
  const fileRef = useRef(null);

  const fetchExhibitors = async () => {
    try {
      const q = query(collection(db, "evento_expositores"), orderBy("nome", "asc"));
      const snapshot = await getDocs(q);
      setExhibitors(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch {
      setError("Erro a carregar dados");
    }
  };

  useEffect(() => { fetchExhibitors(); }, []);

  useEffect(() => {
    if (newExhibitor.logo) {
      if (typeof newExhibitor.logo === "string") setLogoPreview(newExhibitor.logo);
      else setLogoPreview(URL.createObjectURL(newExhibitor.logo));
    } else {
      setLogoPreview(null);
    }
  }, [newExhibitor.logo]);

  const saveField = async (id, field, value) => {
    const docRef = doc(db, "evento_expositores", id);
    await updateDoc(docRef, { [field]: value });
  };

  const saveLogo = async (id) => {
    if (!newExhibitor.logo || typeof newExhibitor.logo === "string") return null;
    const logoRef = ref(storage, `expositores/${newExhibitor.logo.name}`);
    await uploadBytes(logoRef, newExhibitor.logo);
    const url = await getDownloadURL(logoRef);
    await saveField(id, "logo", url);
    return url;
  };

  const deleteExhibitor = async (id) => {
    if (!window.confirm("Tem certeza de que deseja excluir este expositor da app?")) return;
    await deleteDoc(doc(db, "evento_expositores", id));
    setExhibitors(prev => prev.filter(p => p.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const saveAll = async () => {
    if (!newExhibitor.name.trim()) { alert("Nome é obrigatório"); return; }

    try {
      let id = selected?.id;
      let logoURL = selected?.logo || "";

      if (!id || id.startsWith("temp-")) {
        const newDoc = { nome: newExhibitor.name, site: newExhibitor.website, logo: "" };
        const docRef = await addDoc(collection(db, "evento_expositores"), newDoc);
        id = docRef.id;
      } else {
        await saveField(id, "nome", newExhibitor.name);
        await saveField(id, "site", newExhibitor.website);
      }

      if (newExhibitor.logo) logoURL = await saveLogo(id) || logoURL;

      await fetchExhibitors();
      setSelected(null);
      setNewExhibitor({ name: "", website: "", logo: "" });
      setLogoPreview(null);
    } catch {
      alert("Erro ao guardar");
    }
  };

  const exportCSV = () => {
    const headers = ["nome", "site", "logo"];
    const rows = exhibitors.length
      ? exhibitors.map(d => headers.map(h => d[h] || ""))
      : [["", "", ""]];
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(";")).join("\n");
    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "expositores.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const detectSeparator = (line) => (line.includes(";") ? ";" : ",");

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    alert("A importação está a processar...");

    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) throw new Error("Ficheiro sem dados");

      const sep = detectSeparator(lines[0]);
      const headers = lines[0].split(sep).map(h => h.replace(/"/g, "").trim());

      if (!headers.includes("nome")) throw new Error("Estrutura inválida");

      const existing = new Set(exhibitors.map(p => (p.nome || "").toLowerCase().trim()));
      const duplicates = [];
      const newItems = [];
      let added = 0;

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(sep).map(v => v.replace(/"/g, "").trim());
        const obj = {};
        headers.forEach((h, j) => obj[h] = values[j] || "");

        const key = (obj.nome || "").toLowerCase().trim();
        if (!key) continue;
        if (existing.has(key)) duplicates.push(i + 1);
        else { existing.add(key); newItems.push(obj); }
      }

      if (newItems.length === 0 && duplicates.length > 0) {
        alert("Todos os dados já existem.");
        e.target.value = null;
        return;
      }

      for (const obj of newItems) {
        await addDoc(collection(db, "evento_expositores"), obj);
        added++;
      }
      await fetchExhibitors();

      if (duplicates.length > 0) {
        alert(
          `Importação parcial.\n` +
          `${added} inseridos.\n` +
          `Duplicados nas linhas: ${duplicates.join(", ")}`
        );
      } else {
        alert(`Importação concluída. ${added} dados inseridos.`);
      }
    } catch {
      alert("Falha na importação. Verifique o ficheiro.");
    }

    e.target.value = null;
  };

  const filteredExhibitors = exhibitors
    .filter(p => p.nome?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.nome.localeCompare(b.nome));

  if (error) return <p>{error}</p>;

  return (
    <div className="table-container">
      <div>
        <input
          type="text"
          placeholder="Procurar por nome"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input"
        />
        <button className="btn btn-other" onClick={exportCSV}>Exportar CSV</button>
        <label className="btn btn-other">
          Importar CSV
          <input type="file" accept=".csv" style={{ display: "none" }} onChange={handleImportCSV} />
        </label>
      </div>

      {filteredExhibitors.length === 0 ? (
        <p style={{ textAlign: "center", color: "#292f2c" }}>Sem dados</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Logo</th>
              <th>Nome</th>
              <th>Website</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredExhibitors.map(p => (
              <tr key={p.id}>
                <td>
                  {p.logo
                    ? <img src={p.logo} alt="Logo" style={{ width: 50, height: 50, objectFit: "contain" }} />
                    : "—"}
                </td>
                <td>{p.nome || "—"}</td>
                <td>{p.site || "—"}</td>
                <td>
                  <button
                    className="btn btn-add"
                    onClick={() => {
                      setSelected(p);
                      setNewExhibitor({ name: p.nome, website: p.site, logo: p.logo || "" });
                    }}
                  >
                    Editar
                  </button>
                  <button className="btn btn-delete" onClick={() => deleteExhibitor(p.id)}>
                    Apagar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div className="drawer">
          <div className="drawer-content">
            <label>Nome:</label>
            <input
              type="text"
              value={newExhibitor.name}
              onChange={e => setNewExhibitor(prev => ({ ...prev, name: e.target.value }))}
            />

            <br /><br />
            <label>Website:</label>
            <input
              type="text"
              value={newExhibitor.website}
              onChange={e => setNewExhibitor(prev => ({ ...prev, website: e.target.value }))}
            />

            <br /><br />
            <label>Logo:</label>
            {logoPreview && (
              <img
                src={logoPreview}
                alt="Logo"
                style={{ width: 100, height: 100, objectFit: "contain" }}
              />
            )}
            <input
              type="file"
              ref={fileRef}
              onChange={e => {
                const file = e.target.files[0];
                setNewExhibitor(prev => ({ ...prev, logo: file || "" }));
              }}
            />
          </div>

          <div className="drawer-footer">
            <button className="btn btn-add" onClick={saveAll}>Guardar</button>
            <button className="btn btn-cancel" onClick={() => setSelected(null)}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expositores;
